import { Paper, Tooltip, Box } from '@mui/material';
import i18next from 'i18next';
import React, { useEffect, useState, cloneElement } from 'react';

import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import DisplayNumber from '@commons/DisplayNumber';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { calculateChange, calculateColor } from '../utils';

const MetricCard = ({
  title,
  currentValue,
  previousValue,
  isPercentage = false,
  isLoading = false,
  isIncreasePositive = true,
  isCurrency = false,
  disableTooltip = false,
  hasDecimals = false,
}) => {
  const [change, setChange] = useState(null);
  const [color, setColor] = useState(ENUM_COLORS.BLACK);

  useEffect(() => {
    const change = calculateChange(currentValue, previousValue, isPercentage);
    const color = calculateColor(change, isIncreasePositive);

    setChange(change);
    setColor(color);
  }, [currentValue, previousValue]);

  // State to track if tooltip in title is being hovered
  const [titleTooltipHovered, setTitleTooltipHovered] = useState(false);

  // Check if the title contains a React element (indicating it has a tooltip)
  const titleHasTooltip = React.isValidElement(title);
  const shouldDisableTooltip = disableTooltip || titleTooltipHovered;

  // Enhanced title with hover handlers if it contains a tooltip
  const enhancedTitle = titleHasTooltip
    ? cloneElement(title, {
        onMouseEnter: () => setTitleTooltipHovered(true),
        onMouseLeave: () => setTitleTooltipHovered(false),
      })
    : title;

  const cardContent = (
    <Paper
      elevation={2}
      sx={{ p: 2, height: '100%', border: '1px solid #E0E0E0', borderRadius: 2 }}
    >
      <Text color={ENUM_COLORS.DARK} font={ENUM_FONTS.H4}>
        {enhancedTitle}
      </Text>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
          <DeepsightComponentLoader />
        </Box>
      ) : (
        <>
          <Text font={ENUM_FONTS.H1} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <DisplayNumber
              displayCurrencyCode={isCurrency}
              font={ENUM_FONTS.H1}
              number={isPercentage ? Math.round(currentValue * 100) / 100 : currentValue}
              withoutDecimals={!isPercentage && !hasDecimals}
            />
            {isPercentage && '%'}
          </Text>

          {!isNaN(change) && change !== null ? (
            <Text color={color} style={{ display: 'flex', alignItems: 'center' }}>
              {change >= 0 && '+'}
              <DisplayNumber
                color={color}
                font={ENUM_FONTS.H4}
                number={change}
                withoutDecimals={false}
              />
              {isPercentage ? i18next.t('ANALYSIS.PERFORMANCE.PTS') : '%'}
            </Text>
          ) : (
            <Text color={color} style={{ display: 'flex', alignItems: 'center' }}>
              -
            </Text>
          )}
        </>
      )}
    </Paper>
  );

  // Conditionally wrap with Tooltip
  if (shouldDisableTooltip) {
    return cardContent;
  }

  return (
    <Tooltip
      componentsProps={{
        tooltip: {
          sx: {
            padding: '12px 16px',
            backgroundColor: 'rgba(0, 0, 0, 0.87)',
            '& .MuiTooltip-arrow': {
              color: 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      }}
      placement="top"
      title={
        <Text color={ENUM_COLORS.LIGHTEST} font={ENUM_FONTS.H3_INTER}>
          {i18next.t('ANALYSIS.PERFORMANCE.PREVIOUS_PERIOD')}
          {': '}
          {isPercentage ? (
            `${Math.round(previousValue * 100) / 100}%`
          ) : (
            <DisplayNumber
              color={ENUM_COLORS.LIGHTEST}
              displayCurrencyCode={isCurrency}
              font={ENUM_FONTS.H3_INTER}
              number={isPercentage ? Math.round(previousValue * 100) / 100 : previousValue}
              withoutDecimals={!isPercentage && !hasDecimals}
            />
          )}
        </Text>
      }
      arrow
    >
      {cardContent}
    </Tooltip>
  );
};

export default MetricCard;
